import { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import './SmartDraft.css';
import type { SmartDraftData, Hero } from './types';
import { WinRateBar } from './components/WinRateBar';
import { TeamPicker } from './components/TeamPicker';
import { HeroGrid } from './components/HeroGrid';
import { HeroRecommender } from './components/HeroRecommender';
import { LaneAssignmentBanner } from './components/LaneAssignmentBanner';
import { calculateWinRate } from './utils/calculateWinRate';
import { canAssignTeamToDistinctLanes } from './utils/laneAssignment';
import type { PickEvent } from './utils/draftTypes';
import {
  nextPickSeq,
  pickedHeroIdSet,
  replaySlotsFromEvents,
} from './utils/replayDraftSlots';

const WORKER_URL = 'https://mlbb-stats-worker.waddle-dee-production.workers.dev';

export default function SmartDraft() {
  const [data, setData] = useState<SmartDraftData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRecommenderOpen, setIsRecommenderOpen] = useState(false);
  const [pickEvents, setPickEvents] = useState<PickEvent[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${WORKER_URL}/api/stats/smart-draft-data`);
        const result = await response.json();

        if (result.success) {
          setData(result.data);
        } else {
          setError(result.message || 'データの取得に失敗しました');
        }
      } catch (err) {
        setError('ネットワークエラー: データを取得できませんでした');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const heroesMap = useMemo(() => {
    if (!data) {
      return new Map<number, Hero>();
    }
    return new Map(data.heroes.map(h => [h.id, h]));
  }, [data]);

  const { allySlots, enemySlots } = useMemo(() => {
    if (!data) {
      return replaySlotsFromEvents([], new Map());
    }
    return replaySlotsFromEvents(pickEvents, heroesMap);
  }, [pickEvents, data, heroesMap]);

  const allyHeroesData = useMemo(() => {
    if (!data) {
      return [];
    }
    return allySlots
      .map(slot => (slot.heroId ? data.heroes.find(h => h.id === slot.heroId) : null))
      .filter((h): h is Hero => h !== null);
  }, [allySlots, data]);

  const enemyHeroesData = useMemo(() => {
    if (!data) {
      return [];
    }
    return enemySlots
      .map(slot => (slot.heroId ? data.heroes.find(h => h.id === slot.heroId) : null))
      .filter((h): h is Hero => h !== null);
  }, [enemySlots, data]);

  const pickedHeroIds = useMemo(() => pickedHeroIdSet(pickEvents), [pickEvents]);

  const allyLaneWarning = useMemo(
    () => allyHeroesData.length > 0 && !canAssignTeamToDistinctLanes(allyHeroesData),
    [allyHeroesData]
  );
  const enemyLaneWarning = useMemo(
    () => enemyHeroesData.length > 0 && !canAssignTeamToDistinctLanes(enemyHeroesData),
    [enemyHeroesData]
  );

  const { allyRate, enemyRate } = useMemo(() => {
    if (!data) {
      return { allyRate: 0.5, enemyRate: 0.5 };
    }
    const allyRate = calculateWinRate(allyHeroesData, enemyHeroesData);
    return {
      allyRate,
      enemyRate: 1 - allyRate,
    };
  }, [allyHeroesData, enemyHeroesData, data]);

  if (loading) {
    return (
      <div className="smart-draft-page">
        <Header title="スマートドラフト" />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>スマートドラフトデータを読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="smart-draft-page">
        <Header title="スマートドラフト" />
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const handleAllySlotRemove = (index: number) => {
    const slot = allySlots[index];
    if (!slot?.pickSeq) {
      return;
    }
    setPickEvents(pickEvents.filter(e => e.seq !== slot.pickSeq));
  };

  const handleEnemySlotRemove = (index: number) => {
    const slot = enemySlots[index];
    if (!slot?.pickSeq) {
      return;
    }
    setPickEvents(pickEvents.filter(e => e.seq !== slot.pickSeq));
  };

  const handleClearAlly = () => {
    setPickEvents(pickEvents.filter(e => e.team !== 'ally'));
  };

  const handleClearEnemy = () => {
    setPickEvents(pickEvents.filter(e => e.team !== 'enemy'));
  };

  const handleAddToAlly = (heroId: number) => {
    if (!data) {
      return;
    }
    if (!heroesMap.has(heroId) || pickedHeroIds.has(heroId)) {
      return;
    }
    const { allySlots: nextAlly } = replaySlotsFromEvents(pickEvents, heroesMap);
    const emptyIdx = nextAlly.findIndex(s => !s.heroId);
    if (emptyIdx === -1) {
      return;
    }
    const seq = nextPickSeq(pickEvents);
    setPickEvents([...pickEvents, { seq, team: 'ally', slotIndex: emptyIdx, heroId }]);
  };

  const handleAddToEnemy = (heroId: number) => {
    if (!data) {
      return;
    }
    if (!heroesMap.has(heroId) || pickedHeroIds.has(heroId)) {
      return;
    }
    const { enemySlots: nextEnemy } = replaySlotsFromEvents(pickEvents, heroesMap);
    const emptyIdx = nextEnemy.findIndex(s => !s.heroId);
    if (emptyIdx === -1) {
      return;
    }
    const seq = nextPickSeq(pickEvents);
    setPickEvents([...pickEvents, { seq, team: 'enemy', slotIndex: emptyIdx, heroId }]);
  };

  return (
    <div className="smart-draft-page">
      <Header title="スマートドラフト" />
      <div className="draft-layout">
        <div className="draft-main">
          <WinRateBar allyRate={allyRate} enemyRate={enemyRate} />
          <LaneAssignmentBanner allyWarning={allyLaneWarning} enemyWarning={enemyLaneWarning} />
          <div className="team-pickers-container">
            <TeamPicker
              isAlly={true}
              slots={allySlots}
              onSlotClick={() => {}}
              onSlotRemove={handleAllySlotRemove}
              onClearAll={handleClearAlly}
            />
            <TeamPicker
              isAlly={false}
              slots={enemySlots}
              onSlotClick={() => {}}
              onSlotRemove={handleEnemySlotRemove}
              onClearAll={handleClearEnemy}
            />
          </div>
          {data && (
            <HeroGrid
              heroes={data.heroes}
              pickedHeroIds={pickedHeroIds}
              onAddToAlly={handleAddToAlly}
              onAddToEnemy={handleAddToEnemy}
            />
          )}
        </div>
        <div className="draft-sidebar desktop-only">
          {data && (
            <HeroRecommender
              heroes={data.heroes}
              allyHeroes={allyHeroesData}
              enemyHeroes={enemyHeroesData}
              onAddToAlly={handleAddToAlly}
              onAddToEnemy={handleAddToEnemy}
            />
          )}
        </div>
      </div>

      <button className="mobile-recommender-btn" onClick={() => setIsRecommenderOpen(true)}>
        おすすめ{"\n"}ヒーロー
      </button>

      <div
        className={`mobile-drawer-overlay ${isRecommenderOpen ? 'open' : ''}`}
        onClick={() => setIsRecommenderOpen(false)}
      />
      <div className={`mobile-drawer ${isRecommenderOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-header">
          <h3>おすすめヒーロー</h3>
          <button type="button" className="close-btn" onClick={() => setIsRecommenderOpen(false)}>
            ×
          </button>
        </div>
        <div className="mobile-drawer-content">
          {data && (
            <HeroRecommender
              heroes={data.heroes}
              allyHeroes={allyHeroesData}
              enemyHeroes={enemyHeroesData}
              onAddToAlly={handleAddToAlly}
              onAddToEnemy={handleAddToEnemy}
            />
          )}
        </div>
      </div>
    </div>
  );
}
